import { Link } from 'react-router-dom';
import {
  UXP_MC_ID,
  MARKETPLACE_CAPABILITIES,
  AUTHORIZATION_STEPS,
  SYNC_MODES,
  SYNC_CONTROLS,
  SYNC_DOMAINS,
  AI_INIT_ARTIFACTS,
  HEALTH_MONITORS,
  ERROR_FEATURES,
  WEBHOOK_CAPABILITIES,
  ADVISOR_TOPICS,
  MARKETPLACES,
  DEMO_ADVISOR_RECS,
  DEMO_ERRORS,
  DEMO_SYNC_JOBS,
  ARCHITECTURE_STAGES,
  ANALYTICS_METRICS,
  WIZARD_STEPS,
  LIVE_ROUTES,
  buildMarketConnectDashboard,
} from '../../marketconnect/framework';
import {
  ConnectionHealthPanel,
  MarketplaceGrid,
  SyncCommandCenter,
  BrandInitializationPanel,
  ErrorRecoveryCenter,
  WebhookStatusPanel,
  MarketplaceAdvisorCard,
  ConnectionWizardPreview,
} from '../../components/marketconnect';
import { Card, KpiCard } from '../../components/ui';
import { cn } from '../../lib/cn';

const DEMO_HEALTH = [
  { label: 'OAuth', status: 'healthy' as const, detail: 'Token valid · expires in 30d' },
  { label: 'Permissions', status: 'healthy' as const, detail: 'All required scopes granted' },
  { label: 'API Health', status: 'healthy' as const, detail: 'SP-API endpoints OK' },
  { label: 'Rate Limits', status: 'warning' as const, detail: '72% of catalog quota used' },
  { label: 'Background Workers', status: 'healthy' as const, detail: '2 workers active' },
  { label: 'Synchronization', status: 'healthy' as const, detail: 'Last sync 30m ago' },
  { label: 'Marketplace Availability', status: 'healthy' as const, detail: 'Amazon.in operational' },
];

export function MarketplaceConnectionPage() {
  const dashboard = buildMarketConnectDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_MC_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Marketplace Connection Center</h1>
        <p className="mt-1 text-sm text-slate-500">
          Amazon SP-API OAuth, marketplace management, synchronization command center, AI brand initialization, and connection health — the operational heart of marketplace integrations.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Sync Domains" value={dashboard.sync_domains.toString()} />
        <KpiCard label="AI Init Artifacts" value={dashboard.ai_init_artifacts.toString()} />
        <KpiCard label="Marketplaces" value={MARKETPLACES.length.toString()} />
        <KpiCard label="Health Monitors" value={dashboard.health_monitors.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">OAuth Connection Wizard</h2>
        <p className="mt-1 text-sm text-slate-500">PKCE-protected authorization flow — live at <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">/amazon/connect</code></p>
        <div className="mt-4">
          <ConnectionWizardPreview steps={WIZARD_STEPS} currentStep={2} />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Marketplace Grid</h2>
          <Card className="mt-4" padding="sm">
            <MarketplaceGrid marketplaces={MARKETPLACES} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Connection Health</h2>
          <Card className="mt-4" padding="sm">
            <ConnectionHealthPanel metrics={DEMO_HEALTH} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Synchronization Command Center</h2>
        <Card className="mt-4" padding="md">
          <SyncCommandCenter domains={SYNC_DOMAINS} jobs={DEMO_SYNC_JOBS} />
        </Card>
        <div className="mt-3 flex flex-wrap gap-2">
          {SYNC_MODES.map((m) => (
            <span key={m} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{m}</span>
          ))}
          {SYNC_CONTROLS.map((c) => (
            <span key={c} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{c}</span>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <BrandInitializationPanel artifacts={AI_INIT_ARTIFACTS.slice(0, 8)} />
        <MarketplaceAdvisorCard recommendations={DEMO_ADVISOR_RECS} />
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Error Recovery Center</h2>
          <div className="mt-4">
            <ErrorRecoveryCenter errors={DEMO_ERRORS} />
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Webhook Management</h2>
          <div className="mt-4">
            <WebhookStatusPanel capabilities={WEBHOOK_CAPABILITIES} />
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Health Monitors</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {HEALTH_MONITORS.map((m) => (
              <li key={m}>● {m}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Error Management Features</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {ERROR_FEATURES.map((f) => (
              <span key={f} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{f}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Authorization Steps</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {AUTHORIZATION_STEPS.map((s) => (
              <li key={s}>✓ {s}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">All Sync Domains</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {SYNC_DOMAINS.map((d) => (
              <span key={d.domain} className={cn(
                'rounded-lg border px-2 py-1 text-xs',
                d.default ? 'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300' : 'border-slate-200 dark:border-slate-700',
              )}>
                {d.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Initialization Artifacts</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {AI_INIT_ARTIFACTS.map((a) => (
            <span key={a} className="rounded-lg border border-ai-200 bg-ai-50 px-2 py-1 text-xs text-ai-700 dark:border-ai-800 dark:bg-ai-950 dark:text-ai-300">{a}</span>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Marketplace Capabilities</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {MARKETPLACE_CAPABILITIES.map((c) => (
              <span key={c} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{c}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Advisor Topics</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {ADVISOR_TOPICS.map((t) => (
              <span key={t} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{t}</span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Analytics Metrics</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ANALYTICS_METRICS.map((m) => (
            <span key={m} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{m}</span>
          ))}
        </div>
      </section>

      <p className="text-xs text-slate-500">
        Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/uxp/UXP-00003_MARKETPLACE_CONNECTION_CENTER.md</code>
        {' · '}
        Registry: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/marketconnect/</code>
        {' · '}
        Backend: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">services/amazonconnect/</code>
      </p>
    </div>
  );
}
